import React, { useEffect, useRef, useState } from "react";
import moment from "moment";
import api from "../../api";
import { Modal } from "react-bootstrap";
import { notify } from "../../utils/toast";
import { Pagination, Stack } from "@mui/material";
import logo from "../../assets/images/logo.png";

const LIMIT = 10;

export const Fees = () => {
  const [loader, setLoader] = useState(false);
  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");
  const [rollNoSearch, setRollNoSearch] = useState("");
  const [selectedFee, setSelectedFee] = useState(null);
  const [showPayModal, setShowPayModal] = useState(false);
  const [showReceiptModal, setShowReceiptModal] = useState(false);

  const getData = async (
    pageVal = page,
    status = statusFilter,
    rollNo = rollNoSearch,
  ) => {
    try {
      setLoader(true);
      const params = { page: pageVal, limit: LIMIT, status };
      if (rollNo.trim()) params.rollNo = rollNo.trim();
      const response = await api.getFees(params);
      if (response?.success) {
        setData(response?.fees || []);
        setTotal(response?.total || 0);
        setTotalPages(response?.totalPages || 1);
      }
    } catch {
      notify.error("Something went wrong!");
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    getData(1, "");
  }, []);

  const handlePageChange = (e, value) => {
    setPage(value);
    getData(value, statusFilter);
  };

  const handleStatusFilter = (e) => {
    const val = e.target.value;
    setStatusFilter(val);
    setPage(1);
    getData(1, val, rollNoSearch);
  };

  const handleRollNoSearch = (e) => {
    e.preventDefault();
    setPage(1);
    getData(1, statusFilter, rollNoSearch);
  };

  const handleRollNoClear = () => {
    setRollNoSearch("");
    setPage(1);
    getData(1, statusFilter, "");
  };

  const openPayModal = (fee) => {
    setSelectedFee(fee);
    setShowPayModal(true);
  };
  const closePayModal = () => {
    setSelectedFee(null);
    setShowPayModal(false);
  };
  const openReceiptModal = (fee) => {
    setSelectedFee(fee);
    setShowReceiptModal(true);
  };
  const closeReceiptModal = () => {
    setSelectedFee(null);
    setShowReceiptModal(false);
  };

  return (
    <>
      <div className="DashboardMain">
        <div className="row">
          <div className="col">
            {/* Header row */}
            <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
              <h3 className="mb-0">Fees ({total})</h3>
              <div className="d-flex gap-2 align-items-center flex-wrap">
                {/* Roll number search */}
                <form className="d-flex gap-2" onSubmit={handleRollNoSearch}>
                  <input
                    type="text"
                    className="form-control"
                    style={{ width: 180 }}
                    placeholder="Search by roll no…"
                    value={rollNoSearch}
                    onChange={(e) => setRollNoSearch(e.target.value)}
                  />
                  <button type="submit" className="btn btn-outline-dark">
                    <i className="fa-solid fa-magnifying-glass"></i>
                  </button>
                  {rollNoSearch && (
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={handleRollNoClear}
                    >
                      <i className="fa-solid fa-xmark"></i>
                    </button>
                  )}
                </form>
                <select
                  className="form-control"
                  style={{ width: 160 }}
                  value={statusFilter}
                  onChange={handleStatusFilter}
                >
                  <option value="">All Status</option>
                  <option value="unpaid">Unpaid</option>
                  <option value="partial">Partial</option>
                  <option value="paid">Paid</option>
                </select>
              </div>
            </div>

            {loader ? (
              <div className="text-center py-5">
                <div className="spinner-border" role="status" />
              </div>
            ) : (
              <Table
                data={data}
                openPayModal={openPayModal}
                openReceiptModal={openReceiptModal}
              />
            )}

            {data && data.length > 0 && total > LIMIT && (
              <div className="pagination-container">
                <Stack spacing={2}>
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                    sx={{
                      "& .MuiPaginationItem-root.Mui-selected": {
                        backgroundColor: "#10b981",
                        color: "white",
                        "&:hover": { backgroundColor: "#059669" },
                      },
                      "& .MuiPaginationItem-root": {
                        "&:hover": {
                          backgroundColor: "#d1fae5",
                          color: "#059669",
                        },
                      },
                    }}
                  />
                </Stack>
              </div>
            )}
          </div>
        </div>
      </div>

      {selectedFee && showPayModal && (
        <RecordPaymentModal
          show={showPayModal}
          handleClose={closePayModal}
          fee={selectedFee}
          onSuccess={() => getData(page, statusFilter)}
        />
      )}

      {selectedFee && showReceiptModal && (
        <ReceiptModal
          show={showReceiptModal}
          handleClose={closeReceiptModal}
          fee={selectedFee}
        />
      )}
    </>
  );
};

/* ─── Status Badge ─── */
const StatusBadge = ({ status }) => {
  const map = {
    paid: { cls: "bg-success", label: "Paid" },
    partial: { cls: "bg-warning text-dark", label: "Partial" },
    unpaid: { cls: "bg-danger", label: "Unpaid" },
  };
  const { cls, label } = map[status] || { cls: "bg-secondary", label: status };
  return <span className={`badge ${cls}`}>{label}</span>;
};

/* ─── Table ─── */
const Table = ({ data, openPayModal, openReceiptModal }) => (
  <div className="TableMain mt-3">
    <div className="table-responsive">
      <table className="table table-hover drivers-table">
        <thead>
          <tr>
            <th>Student</th>
            <th>Roll No.</th>
            <th>Plan</th>
            <th>Total Amount</th>
            <th>Paid Amount</th>
            <th>Remaining</th>
            <th>Due Date</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {data.length > 0 ? (
            data.map((item, key) => {
              const remaining = item?.amount - item?.paidAmount;
              return (
                <tr key={key}>
                  <td style={{ textTransform: "capitalize" }}>
                    <div className="table-text-dark">
                      {item?.studentId?.name || "-"}
                    </div>
                  </td>
                  <td style={{ textTransform: "uppercase" }}>
                    <span className="table-text-dark">
                      {item?.studentId?.rollNo || "-"}
                    </span>
                  </td>
                  <td style={{ textTransform: "capitalize" }}>
                    <span className="table-text-dark">{item?.plan}</span>
                  </td>
                  <td>
                    <span className="table-text-dark">
                      {item?.amount?.toLocaleString()}
                    </span>
                  </td>
                  <td>
                    <span className="table-text-dark">
                      {item?.paidAmount?.toLocaleString()}
                    </span>
                  </td>
                  <td>
                    <span
                      className={`table-text-dark ${remaining > 0 ? "text-danger fw-bold" : ""}`}
                    >
                      {remaining?.toLocaleString()}
                    </span>
                  </td>
                  <td>
                    <span className="table-text-dark">
                      {moment(item?.dueDate).format("MMM Do YYYY")}
                    </span>
                  </td>
                  <td>
                    <StatusBadge status={item?.status} />
                  </td>
                  <td>
                    <div className="d-flex gap-2">
                      {item?.status !== "paid" && (
                        <button
                          className="btn btn-sm btn-outline-success"
                          onClick={() => openPayModal(item)}
                        >
                          <i className="fa-solid fa-money-bill me-1"></i>Pay
                        </button>
                      )}
                      {item?.paidAmount > 0 && (
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => openReceiptModal(item)}
                        >
                          <i className="fa-solid fa-receipt me-1"></i>Receipt
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })
          ) : (
            <tr>
              <td colSpan={9} className="text-center py-4 text-muted">
                No fee records found
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  </div>
);

/* ─── Record Payment Modal ─── */
const RecordPaymentModal = ({ show, handleClose, fee, onSuccess }) => {
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("cash");
  const [loader, setLoader] = useState(false);

  const remaining = fee?.amount - fee?.paidAmount;

  const handleSubmit = async () => {
    const numAmount = Number(amount);
    if (!numAmount || numAmount <= 0) {
      notify.error("Please enter a valid amount");
      return;
    }
    if (numAmount > remaining) {
      notify.error(`Amount cannot exceed remaining balance (${remaining})`);
      return;
    }
    try {
      setLoader(true);
      const response = await api.recordFeePayment(fee._id, {
        amount: numAmount,
        method,
      });
      if (response?.success) {
        notify.success(
          `Payment recorded! Receipt: ${response.payment?.receiptNo}`,
        );
        onSuccess();
        handleClose();
        setAmount("");
        setMethod("cash");
      }
    } catch {
      notify.error("Something went wrong!");
    } finally {
      setLoader(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose}>
      <div className="taskForm">
        <div className="closeIcon" onClick={handleClose}>
          <i className="fa-solid fa-xmark" />
        </div>
        <div className="h3">Record Payment</div>
        <div className="mb-3 p-3 rounded" style={{ background: "#f8f9fa" }}>
          <div className="d-flex justify-content-between mb-1">
            <span className="text-muted">Student</span>
            <strong>{fee?.studentId?.name}</strong>
          </div>
          <div className="d-flex justify-content-between mb-1">
            <span className="text-muted">Total Fee</span>
            <strong>{fee?.amount?.toLocaleString()}</strong>
          </div>
          <div className="d-flex justify-content-between mb-1">
            <span className="text-muted">Already Paid</span>
            <strong className="text-success">
              {fee?.paidAmount?.toLocaleString()}
            </strong>
          </div>
          <div className="d-flex justify-content-between">
            <span className="text-muted">Remaining</span>
            <strong className="text-danger">
              {remaining?.toLocaleString()}
            </strong>
          </div>
        </div>
        <div className="row">
          <div className="col-12">
            <div className="form-group">
              <label>Payment Amount</label>
              <input
                type="number"
                className="form-control"
                placeholder={`Max: ${remaining}`}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
            </div>
          </div>
          <div className="col-12">
            <div className="form-group">
              <label>Payment Method</label>
              <select
                className="form-control"
                value={method}
                onChange={(e) => setMethod(e.target.value)}
              >
                <option value="cash">Cash</option>
                <option value="card">Card</option>
                <option value="online">Online</option>
              </select>
            </div>
          </div>
        </div>
        <div className="mt-4">
          <button
            className="themeBtn w-100 text-center"
            onClick={handleSubmit}
            disabled={loader}
          >
            {loader ? "Processing..." : "Record Payment"}
          </button>
        </div>
      </div>
    </Modal>
  );
};

/* ─── Shared receipt print styles ─── */
const RECEIPT_STYLES = `
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: Arial, sans-serif; background: #fff; color: #222; }
  .receipt-wrapper { width: 700px; margin: 30px auto; padding: 40px; border: 2px solid #10b981; border-radius: 12px; }
  .receipt-header { display: flex; justify-content: space-between; align-items: center; border-bottom: 2px solid #10b981; padding-bottom: 20px; margin-bottom: 24px; }
  .receipt-header img { height: 60px; }
  .receipt-header-right { text-align: right; }
  .receipt-header-right h2 { color: #10b981; font-size: 22px; margin-bottom: 4px; }
  .receipt-header-right p { font-size: 12px; color: #666; }
  .receipt-title { text-align: center; font-size: 20px; font-weight: bold; letter-spacing: 2px; color: #10b981; margin-bottom: 24px; text-transform: uppercase; }
  .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px 32px; margin-bottom: 24px; }
  .info-item label { font-size: 11px; color: #888; text-transform: uppercase; letter-spacing: 1px; display: block; margin-bottom: 2px; }
  .info-item span { font-size: 14px; font-weight: 600; color: #222; }
  .payment-history-title { font-size: 13px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; color: #10b981; margin-bottom: 10px; }
  .payment-table { width: 100%; border-collapse: collapse; margin-bottom: 24px; }
  .payment-table th { background: #10b981; color: white; padding: 10px 14px; text-align: left; font-size: 13px; }
  .payment-table td { padding: 10px 14px; border-bottom: 1px solid #e5e7eb; font-size: 13px; }
  .payment-table tr:last-child td { border-bottom: none; }
  .summary-box { background: #f0fdf4; border: 1px solid #10b981; border-radius: 8px; padding: 16px 20px; margin-bottom: 24px; }
  .summary-row { display: flex; justify-content: space-between; margin-bottom: 8px; font-size: 14px; }
  .summary-row:last-child { margin-bottom: 0; border-top: 1px dashed #10b981; padding-top: 8px; font-weight: bold; font-size: 15px; }
  .status-badge { display: inline-block; padding: 4px 14px; border-radius: 20px; font-size: 12px; font-weight: bold; text-transform: uppercase; }
  .status-paid    { background: #d1fae5; color: #065f46; }
  .status-partial { background: #fef3c7; color: #92400e; }
  .status-unpaid  { background: #fee2e2; color: #991b1b; }
  .receipt-footer { text-align: center; font-size: 11px; color: #aaa; border-top: 1px solid #e5e7eb; padding-top: 16px; margin-top: 8px; }
  @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
`;

const printContent = (html, title) => {
  const w = window.open("", "_blank", "width=800,height=600");
  w.document.write(
    `<!DOCTYPE html><html><head><title>${title}</title><style>${RECEIPT_STYLES}</style></head><body>${html}</body></html>`,
  );
  w.document.close();
  w.focus();
  setTimeout(() => {
    w.print();
    w.close();
  }, 300);
};

/* ─── Receipt Modal — all payments for a fee ─── */
const ReceiptModal = ({ show, handleClose, fee }) => {
  const receiptRef = useRef();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        setLoading(true);
        const res = await api.getFeePayments(fee._id);
        if (res?.success) setPayments(res.payments || []);
      } catch {
        notify.error("Could not load payments");
      } finally {
        setLoading(false);
      }
    };
    if (show) fetch();
  }, [show, fee._id]);

  const receiptNo = `RCP-${fee?._id?.slice(-6).toUpperCase()}`;
  const statusClass =
    { paid: "status-paid", partial: "status-partial", unpaid: "status-unpaid" }[
      fee?.status
    ] || "";

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <div className="taskForm" style={{ maxWidth: "100%" }}>
        <div className="closeIcon" onClick={handleClose}>
          <i className="fa-solid fa-xmark" />
        </div>
        <div className="d-flex justify-content-end gap-2 mb-3">
          <button
            className="btn btn-success"
            onClick={() =>
              printContent(
                receiptRef.current.innerHTML,
                `Fee Receipt - ${fee?.studentId?.name}`,
              )
            }
          >
            <i className="fa-solid fa-print me-2"></i>Print / Save PDF
          </button>
        </div>

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border" role="status" />
          </div>
        ) : (
          <div ref={receiptRef}>
            <div className="receipt-wrapper">
              <div className="receipt-header">
                <img src={logo} alt="Logo" style={{ height: 60 }} />
                <div className="receipt-header-right">
                  <h2 style={{ color: "#10b981", marginBottom: 4 }}>
                    Shabbir Transport
                  </h2>
                  <p style={{ fontSize: 12, color: "#666" }}>
                    Fee Ref: <strong>{receiptNo}</strong>
                  </p>
                  <p style={{ fontSize: 12, color: "#666" }}>
                    Printed: {moment().format("DD MMM YYYY, hh:mm A")}
                  </p>
                </div>
              </div>

              <div className="receipt-title">Fee Payment Receipt</div>

              <div className="info-grid">
                <div className="info-item">
                  <label>Student Name</label>
                  <span>{fee?.studentId?.name || "-"}</span>
                </div>
                <div className="info-item">
                  <label>Roll Number</label>
                  <span>{fee?.studentId?.rollNo || "-"}</span>
                </div>
                <div className="info-item">
                  <label>Fee Plan</label>
                  <span style={{ textTransform: "capitalize" }}>
                    {fee?.plan}
                  </span>
                </div>
                <div className="info-item">
                  <label>Due Date</label>
                  <span>{moment(fee?.dueDate).format("DD MMM YYYY")}</span>
                </div>
                <div className="info-item">
                  <label>Status</label>
                  <span className={`status-badge ${statusClass}`}>
                    {fee?.status}
                  </span>
                </div>
                <div className="info-item">
                  <label>Route</label>
                  <span>{fee?.studentId?.routeId?.name || "-"}</span>
                </div>
              </div>

              {payments.length > 0 && (
                <>
                  <div className="payment-history-title">Payment History</div>
                  <table className="payment-table">
                    <thead>
                      <tr>
                        <th>Receipt No.</th>
                        <th>Date</th>
                        <th>Method</th>
                        <th>Amount (PKR)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payments.map((p) => (
                        <tr key={p._id}>
                          <td>
                            <strong>{p.receiptNo}</strong>
                          </td>
                          <td>
                            {moment(p.createdAt).format("DD MMM YYYY, hh:mm A")}
                          </td>
                          <td style={{ textTransform: "capitalize" }}>
                            {p.method}
                          </td>
                          <td>
                            <strong>{p.amount?.toLocaleString()}</strong>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}

              <div className="summary-box">
                <div className="summary-row">
                  <span>Total Fee</span>
                  <span>PKR {fee?.amount?.toLocaleString()}</span>
                </div>
                <div className="summary-row">
                  <span>Amount Paid</span>
                  <span style={{ color: "#10b981" }}>
                    PKR {fee?.paidAmount?.toLocaleString()}
                  </span>
                </div>
                <div className="summary-row">
                  <span>Balance Remaining</span>
                  <span
                    style={{
                      color:
                        fee?.amount - fee?.paidAmount > 0
                          ? "#dc2626"
                          : "#10b981",
                    }}
                  >
                    PKR {(fee?.amount - fee?.paidAmount)?.toLocaleString()}
                  </span>
                </div>
              </div>

              <div className="receipt-footer">
                <p>
                  This is a computer-generated receipt. No signature required.
                </p>
                <p style={{ marginTop: 4 }}>
                  Shabbir Transport &mdash; Thank you for your payment
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};
